import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Card, CardContent } from '@/components/ui/card'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Phone, Plus, Search, ArrowRight, UserCircle, Loader2 } from 'lucide-react'
import { useMembros } from '@/hooks/use-membros'
import { useChurchColors } from '@/hooks/useChurchColors'
import { getInitials } from '@/lib/utils'
import type { TipoPermissao } from '@/types'

// Cores para os diferentes perfis/permissões
const PAPEL_COLORS: Record<NonNullable<TipoPermissao>, string> = {
  admin: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300 border-red-200 dark:border-red-800',
  gestor: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300 border-green-200 dark:border-green-800',
  lider: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800',
  membro: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 border-blue-200 dark:border-blue-800',
}

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
}

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 },
}

export function MembrosPage() {
  const navigate = useNavigate()
  const { data: membros = [], isLoading, error } = useMembros()
  const { primaryColor } = useChurchColors()
  const [searchTerm, setSearchTerm] = useState('')

  // Filtrar membros baseado no termo de busca
  const filteredMembros = membros.filter((membro) =>
    membro.nome.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Loading state
  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="ml-2">Carregando membros...</span>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="container mx-auto p-6">
        <div className="rounded-lg border border-destructive/20 bg-destructive/10 p-6 text-center">
          <h3 className="mb-2 text-lg font-semibold text-destructive">Erro ao carregar membros</h3>
          <p className="text-sm text-muted-foreground">
            {error instanceof Error ? error.message : 'Ocorreu um erro inesperado'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
      >
        <div className="space-y-1">
          <h1 className="text-3xl font-bold tracking-tight md:text-4xl">Membros</h1>
          <p className="text-muted-foreground">Visualize e gerencie os membros da igreja</p>
        </div>
        <Button
          size="lg"
          className="shadow-md"
          style={{ 
            backgroundColor: primaryColor,
            borderColor: primaryColor,
            boxShadow: `0 4px 14px 0 ${primaryColor}33`
          }}
          onClick={() => navigate('/membros/novo')}
        >
          <Plus className="mr-2 h-4 w-4" />
          Adicionar Membro
        </Button>
      </motion.div>

      {/* Search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="relative"
      >
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Buscar membros por nome..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </motion.div>

      {/* Lista de Membros */}
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="space-y-2"
      >
        {filteredMembros.map((membro) => (
          <motion.div key={membro.id} variants={item}>
            <Card 
              className="group overflow-hidden transition-all hover:shadow-md"
              style={{ 
                boxShadow: `0 4px 6px -1px ${primaryColor}1a, 0 2px 4px -2px ${primaryColor}1a`
              }}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <Avatar 
                        className="h-12 w-12 border-2 border-background shadow-sm"
                        style={{ 
                          boxShadow: `0 0 0 1px ${primaryColor}1a`
                        }}
                      >
                        <AvatarImage src={undefined} />
                        <AvatarFallback 
                          className="text-sm text-white"
                          style={{ 
                            background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}b3)`
                          }}
                        >
                          {getInitials(membro.nome)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border border-background bg-emerald-500" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3">
                        <h3 
                          className="font-semibold text-foreground group-hover:transition-colors truncate"
                          onMouseEnter={(e) => e.currentTarget.style.color = primaryColor}
                          onMouseLeave={(e) => e.currentTarget.style.color = ''}
                        >
                          {membro.nome}
                        </h3>
                        <Badge 
                          variant="secondary" 
                          className={`capitalize text-xs ${PAPEL_COLORS[membro.permissao as TipoPermissao] || PAPEL_COLORS.membro}`}
                        >
                          {membro.permissao}
                        </Badge>
                      </div>
                      
                      {membro.telefone && (
                        <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                          <Phone className="h-3 w-3" />
                          <span>{membro.telefone}</span>
                        </div>
                      )}
                      
                      {membro.email && (
                        <div className="text-sm text-muted-foreground mt-0.5 truncate">
                          {membro.email}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/membros/${membro.id}`)}
                    className="shrink-0"
                  >
                    Ver Perfil
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </motion.div>

      {filteredMembros.length === 0 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="rounded-lg border bg-card p-12 text-center"
        >
          <UserCircle className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
          <h3 className="mb-2 text-lg font-semibold">Nenhum membro encontrado</h3>
          <p className="text-sm text-muted-foreground">
            Tente ajustar sua pesquisa ou adicione um novo membro
          </p>
        </motion.div>
      )}
    </div>
  )
}
